import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';

const BRANDS: Record<string, string> = { zkteco: 'ZKTeco', hikvision: 'Hikvision', dahua: 'Dahua' };
const CTRL_TYPES: Record<string, string> = { entrance: 'Орцны хаалга', elevator: 'Лифт' };
const EVENT_TYPES: Record<string, { label: string; color: string }> = {
  granted: { label: 'Зөвшөөрсөн', color: '#10b981' },
  denied: { label: 'Татгалзсан', color: '#ef4444' },
  door_forced: { label: 'Хаалга эвдсэн', color: '#dc2626' },
  door_held: { label: 'Хаалга нээлттэй', color: '#f59e0b' },
  remote_open: { label: 'Зайнаас нээсэн', color: '#3b82f6' },
};
const CARD_STATUSES: Record<string, { label: string; color: string }> = {
  active: { label: 'Идэвхтэй', color: '#10b981' },
  suspended: { label: 'Түтгэлзүүлсэн', color: '#f59e0b' },
  lost: { label: 'Алдсан', color: '#ef4444' },
  expired: { label: 'Хугацаа дууссан', color: '#6b7280' },
};

export default function AccessControlPage() {
  const [tab, setTab] = useState<'controllers' | 'cards' | 'events'>('controllers');

  // controllers
  const [ctrls, setCtrls] = useState<any[]>([]);
  const [ctrlLoading, setCtrlLoading] = useState(true);
  const [showCtrlForm, setShowCtrlForm] = useState(false);
  const [editCtrl, setEditCtrl] = useState<any>(null);
  const [ctrlForm, setCtrlForm] = useState({
    name: '', brand: 'zkteco', controller_type: 'entrance', location: '',
    ip_address: '', port: 80, username: 'admin', password: '', door_count: 1, allowed_floors: '',
  });

  // cards
  const [cards, setCards] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(true);
  const [showCardForm, setShowCardForm] = useState(false);
  const [editCard, setEditCard] = useState<any>(null);
  const [cardForm, setCardForm] = useState({ card_number: '', card_label: '', unit_id: '', resident_id: '', notes: '' });
  const [cardSearch, setCardSearch] = useState('');
  const [cardPage, setCardPage] = useState(0);
  const PAGE_SIZE = 50;

  // events
  const [events, setEvents] = useState<any[]>([]);
  const [evLoading, setEvLoading] = useState(true);
  const [evPage, setEvPage] = useState(0);
  const [evCard, setEvCard] = useState('');
  const [evType, setEvType] = useState('');

  // lookups
  const [units, setUnits] = useState<any[]>([]);
  const [residents, setResidents] = useState<any[]>([]);

  useEffect(() => { loadUnits(); loadResidents(); }, []);
  useEffect(() => {
    if (tab === 'controllers') loadCtrls();
    else if (tab === 'cards') loadCards();
    else loadEvents();
  }, [tab]);

  const loadUnits = async () => { try { const r = await api.get<any>('/admin/units?limit=1000'); setUnits(r.items || []); } catch {} };
  const loadResidents = async () => { try { const r = await api.get<any>('/admin/residents'); setResidents(r.items || r || []); } catch {} };

  const loadCtrls = async () => {
    setCtrlLoading(true);
    try { const r = await api.get<any>('/admin/access/controllers'); setCtrls(r.items || []); }
    catch {} finally { setCtrlLoading(false); }
  };

  const loadCards = async () => {
    setCardLoading(true);
    try {
      const qs = cardSearch ? `?search=${encodeURIComponent(cardSearch)}` : '';
      const r = await api.get<any>(`/admin/access/cards${qs}`);
      setCards(r.items || []);
      setCardPage(0);
    } catch {} finally { setCardLoading(false); }
  };

  const loadEvents = async () => {
    setEvLoading(true);
    try {
      let qs = '?per_page=100';
      if (evCard) qs += `&card=${encodeURIComponent(evCard)}`;
      if (evType) qs += `&event_type=${evType}`;
      const r = await api.get<any>(`/admin/access/events${qs}`);
      setEvents(r.items || []);
      setEvPage(0);
    } catch {} finally { setEvLoading(false); }
  };

  // controller CRUD
  const openCtrlForm = (c?: any) => {
    if (c) {
      setEditCtrl(c);
      setCtrlForm({ name: c.name, brand: c.brand, controller_type: c.controller_type, location: c.location, ip_address: c.ip_address, port: c.port, username: c.username, password: '', door_count: c.door_count, allowed_floors: c.allowed_floors || '' });
    } else {
      setEditCtrl(null);
      setCtrlForm({ name: '', brand: 'zkteco', controller_type: 'entrance', location: '', ip_address: '', port: 80, username: 'admin', password: '', door_count: 1, allowed_floors: '' });
    }
    setShowCtrlForm(true);
  };

  const saveCtrl = async () => {
    try {
      if (editCtrl) {
        const p: any = { ...ctrlForm };
        if (!p.password) delete p.password;
        await api.put(`/admin/access/controllers/${editCtrl.id}`, p);
      } else {
        await api.post('/admin/access/controllers', ctrlForm);
      }
      setShowCtrlForm(false); loadCtrls();
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const deleteCtrl = async (id: string) => {
    if (!window.confirm('Контроллер устгах уу?')) return;
    try { await api.delete(`/admin/access/controllers/${id}`); loadCtrls(); } catch {}
  };

  const testCtrl = async (id: string) => {
    try {
      const r = await api.post<any>(`/admin/access/controllers/${id}/test`);
      alert(`${r.controller}: ${r.health?.status || 'unknown'}`);
    } catch (e: any) { alert(e?.body || 'Холбогдож чадсангүй'); }
  };

  const unlockDoor = async (id: string, door: number = 0) => {
    if (!window.confirm('Хаалгыг зайнаас нээх үү?')) return;
    try {
      await api.post<any>(`/admin/access/controllers/${id}/unlock?door=${door}`);
      alert('Хаалга нээгдлээ!');
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  // card CRUD
  const openCardForm = (c?: any) => {
    if (c) {
      setEditCard(c);
      setCardForm({ card_number: c.card_number, card_label: c.card_label, unit_id: c.unit_id || '', resident_id: c.resident_id || '', notes: c.notes || '' });
    } else {
      setEditCard(null);
      setCardForm({ card_number: '', card_label: '', unit_id: '', resident_id: '', notes: '' });
    }
    setShowCardForm(true);
  };

  const saveCard = async () => {
    try {
      const payload = { ...cardForm, unit_id: cardForm.unit_id || undefined, resident_id: cardForm.resident_id || undefined };
      if (editCard) await api.put(`/admin/access/cards/${editCard.id}`, payload);
      else await api.post('/admin/access/cards', payload);
      setShowCardForm(false); loadCards();
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const deleteCard = async (id: string) => {
    if (!window.confirm('Карт устгах уу?')) return;
    try { await api.delete(`/admin/access/cards/${id}`); loadCards(); } catch {}
  };

  const suspendCard = async (id: string) => {
    if (!window.confirm('Картыг түтгэлзүүлэх үү? Бүх контроллероос устгана.')) return;
    try { await api.post(`/admin/access/cards/${id}/suspend`); loadCards(); } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const syncCard = async (id: string) => {
    try {
      const r = await api.post<any>(`/admin/access/cards/${id}/sync`);
      const summary = r.results?.map((x: any) => `${x.controller}: ${x.success ? '✓' : '✗'}`).join(', ');
      alert(`Карт: ${r.card}\n${summary || 'Контроллер олдсонгүй'}`);
    } catch (e: any) { alert(e?.body || 'Алдаа'); }
  };

  const getUnitLabel = (id: string) => { const u = units.find((u: any) => u.id === id); return u ? `${u.building}-${u.unit_number}` : id?.substring(0, 8); };
  const getResidentLabel = (id: string) => { const r = residents.find((r: any) => r.id === id); return r ? `${r.first_name} ${r.last_name || ''}`.trim() : id?.substring(0, 8); };

  const S: Record<string, React.CSSProperties> = {
    card: { background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)', border: '1px solid #e8e4db' },
    btn: { padding: '10px 20px', background: 'linear-gradient(135deg, #2f7d52, #236844)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    input: { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #d5d0c4', fontSize: 14, background: '#faf9f5' },
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { background: '#fff', borderRadius: 16, padding: 28, width: '95%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' as const },
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Inter'", fontSize: 26, fontWeight: 700, color: '#1a3a2a', margin: 0 }}>Нэвтрэх хяналт</h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>Орцны хаалга, лифтны чип удирдлага</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#e8e4db', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {([['controllers', 'Контроллерууд'], ['cards', 'Картууд'], ['events', 'Нэвтрэлтийн лог']] as const).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t as any)} style={{
            padding: '10px 24px', border: 'none', borderRadius: 10, cursor: 'pointer',
            background: tab === t ? '#fff' : 'transparent', color: tab === t ? '#236844' : '#6b7280',
            fontWeight: tab === t ? 600 : 400, fontSize: 14, boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
          }}>{label}</button>
        ))}
      </div>

      {/* ═══ CONTROLLERS ═══ */}
      {tab === 'controllers' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontFamily: "'Inter'", fontSize: 18, fontWeight: 700, color: '#1a3a2a' }}>Нэвтрэх контроллерууд</h3>
            <button onClick={() => openCtrlForm()} style={S.btn}>+ Контроллер нэмэх</button>
          </div>
          {ctrlLoading ? <p style={{ color: '#6b7280' }}>Уншиж байна...</p> : ctrls.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#6b7280', padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🚪</div>
              <p>Бүртгэлтэй контроллер байхгүй</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 16 }}>
              {ctrls.map((c: any) => (
                <div key={c.id} style={{ background: '#faf9f5', borderRadius: 14, padding: 20, border: '1px solid #e8e4db' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a3a2a' }}>{c.name}</h4>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#dbeafe', color: '#1d4ed8' }}>{BRANDS[c.brand]}</span>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: c.controller_type === 'entrance' ? '#dcfce7' : '#fef3c7', color: c.controller_type === 'entrance' ? '#236844' : '#92400e' }}>
                          {CTRL_TYPES[c.controller_type]}
                        </span>
                      </div>
                    </div>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.is_active ? '#10b981' : '#d1d5db', marginTop: 4 }} />
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
                    <div>{c.location || 'Байршил тодорхойгүй'}</div>
                    <div>IP: <strong style={{ color: '#374151' }}>{c.ip_address}:{c.port}</strong> · Хаалга: {c.door_count}</div>
                    {c.last_heartbeat && <div>Сүүлд: {new Date(c.last_heartbeat).toLocaleString('mn-MN')}</div>}
                  </div>
                  <div style={{ background: '#f4f1e8', borderRadius: 10, padding: 10, marginBottom: 10 }}>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>Webhook URL:</div>
                    <code style={{ fontSize: 11, color: '#236844', wordBreak: 'break-all' as const }}>
                      {window.location.origin.replace('admin.', 'api.').replace(':3000', ':8000')}/api/v1{c.webhook_url}
                    </code>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                    <button onClick={() => testCtrl(c.id)} style={{ ...S.btn, padding: '6px 12px', fontSize: 13, background: '#2563eb' }}>Тест</button>
                    <button onClick={() => unlockDoor(c.id)} style={{ ...S.btn, padding: '6px 12px', fontSize: 13, background: '#f59e0b' }}>Хаалга нээх</button>
                    <button onClick={() => openCtrlForm(c)} style={{ background: 'none', border: '1px solid #d5d0c4', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>Засах</button>
                    <button onClick={() => deleteCtrl(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500 }}>Устгах</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ CARDS ═══ */}
      {tab === 'cards' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input placeholder="Картын дугаараар хайх..." value={cardSearch} onChange={e => setCardSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadCards()} style={{ ...S.input, width: 260 }} />
              <button onClick={loadCards} style={{ ...S.btn, background: '#e8e4db', color: '#1a3a2a' }}>Хайх</button>
            </div>
            <button onClick={() => openCardForm()} style={S.btn}>+ Карт бүртгэх</button>
          </div>
          {cardLoading ? <p style={{ color: '#6b7280' }}>Уншиж байна...</p> : (
           <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8e4db' }}>
                  {['№', 'Картын дугаар', 'Нэр', 'Тоот', 'Оршин суугч', 'Төлөв', 'Огноо', ''].map((h, i) => (
                    <th key={i} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cards.slice(cardPage * PAGE_SIZE, (cardPage + 1) * PAGE_SIZE).map((c: any, i: number) => {
                  const st = CARD_STATUSES[c.status] || { label: c.status, color: '#6b7280' };
                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f0ede5' }}>
                      <td style={{ padding: '12px 8px', fontSize: 14, color: '#8a978f' }}>{cardPage * PAGE_SIZE + i + 1}</td>
                      <td style={{ padding: '12px 8px', fontWeight: 700, fontSize: 14, fontFamily: "'Inter'", color: '#1a3a2a', letterSpacing: 0.5 }}>{c.card_number}</td>
                      <td style={{ padding: '12px 8px', fontSize: 14 }}>{c.card_label || '-'}</td>
                      <td style={{ padding: '12px 8px', fontSize: 14 }}>{c.unit_id ? getUnitLabel(c.unit_id) : '-'}</td>
                      <td style={{ padding: '12px 8px', fontSize: 14 }}>{c.resident_id ? getResidentLabel(c.resident_id) : '-'}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: st.color + '18', color: st.color }}>{st.label}</span>
                      </td>
                      <td style={{ padding: '12px 8px', fontSize: 13, color: '#6b7280' }}>{c.issued_at ? new Date(c.issued_at).toLocaleDateString('mn-MN') : '-'}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => syncCard(c.id)} title="Контроллерт синк" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 13, fontWeight: 500 }}>Синк</button>
                          {c.status === 'active' && <button onClick={() => suspendCard(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', fontSize: 13, fontWeight: 500 }}>Түтгэлзүүлэх</button>}
                          <button onClick={() => openCardForm(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2f7d52', fontSize: 13, fontWeight: 500 }}>Засах</button>
                          <button onClick={() => deleteCard(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, fontWeight: 500 }}>Устгах</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {cards.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Бүртгэлтэй карт байхгүй</td></tr>}
              </tbody>
            </table>
            {cards.length > 0 && (
              <Pagination page={cardPage} pageSize={PAGE_SIZE} total={cards.length}
                count={cards.slice(cardPage * PAGE_SIZE, (cardPage + 1) * PAGE_SIZE).length} onChange={setCardPage} />
            )}
           </>
          )}
        </div>
      )}

      {/* ═══ EVENTS ═══ */}
      {tab === 'events' && (
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontFamily: "'Inter'", fontSize: 18, fontWeight: 700, color: '#1a3a2a' }}>Нэвтрэлтийн лог</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input placeholder="Картын дугаар..." value={evCard} onChange={e => setEvCard(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadEvents()} style={{ ...S.input, width: 180 }} />
              <select value={evType} onChange={e => setEvType(e.target.value)} style={{ ...S.input, width: 160 }}>
                <option value="">Бүх төрөл</option>
                {Object.entries(EVENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button onClick={loadEvents} style={S.btn}>Шүүх</button>
            </div>
          </div>
          {evLoading ? <p style={{ color: '#6b7280' }}>Уншиж байна...</p> : (
           <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8e4db' }}>
                  {['№', 'Огноо', 'Картын дугаар', 'Төрөл', 'Хаалга'].map((h, i) => (
                    <th key={i} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.slice(evPage * PAGE_SIZE, (evPage + 1) * PAGE_SIZE).map((e: any, i: number) => {
                  const et = EVENT_TYPES[e.event_type] || { label: e.event_type, color: '#6b7280' };
                  return (
                    <tr key={e.id} style={{ borderBottom: '1px solid #f0ede5' }}>
                      <td style={{ padding: '10px 8px', fontSize: 13, color: '#8a978f' }}>{evPage * PAGE_SIZE + i + 1}</td>
                      <td style={{ padding: '10px 8px', fontSize: 13, color: '#6b7280' }}>{new Date(e.event_time).toLocaleString('mn-MN')}</td>
                      <td style={{ padding: '10px 8px', fontWeight: 700, fontSize: 14, fontFamily: "'Inter'", color: '#1a3a2a' }}>{e.card_number}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <span style={{ padding: '2px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: et.color + '18', color: et.color }}>{et.label}</span>
                      </td>
                      <td style={{ padding: '10px 8px', fontSize: 13 }}>Хаалга {e.door_index + 1}</td>
                    </tr>
                  );
                })}
                {events.length === 0 && <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Нэвтрэлтийн бүртгэл байхгүй</td></tr>}
              </tbody>
            </table>
            {events.length > 0 && (
              <Pagination page={evPage} pageSize={PAGE_SIZE} total={events.length}
                count={events.slice(evPage * PAGE_SIZE, (evPage + 1) * PAGE_SIZE).length} onChange={setEvPage} />
            )}
           </>
          )}
        </div>
      )}

      {/* ═══ CONTROLLER MODAL ═══ */}
      {showCtrlForm && (
        <div style={S.overlay} onClick={() => setShowCtrlForm(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1a3a2a', marginTop: 0 }}>
              {editCtrl ? 'Контроллер засах' : 'Контроллер нэмэх'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Нэр *</label>
                <input value={ctrlForm.name} onChange={e => setCtrlForm({ ...ctrlForm, name: e.target.value })} style={S.input} placeholder="1-р орц хаалга" /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Брэнд</label>
                <select value={ctrlForm.brand} onChange={e => setCtrlForm({ ...ctrlForm, brand: e.target.value })} style={S.input}>
                  {Object.entries(BRANDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Төрөл</label>
                <select value={ctrlForm.controller_type} onChange={e => setCtrlForm({ ...ctrlForm, controller_type: e.target.value })} style={S.input}>
                  {Object.entries(CTRL_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div style={{ gridColumn: '1/-1' }}><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Байршил</label>
                <input value={ctrlForm.location} onChange={e => setCtrlForm({ ...ctrlForm, location: e.target.value })} style={S.input} placeholder="А байр, 1-р орц" /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>IP хаяг *</label>
                <input value={ctrlForm.ip_address} onChange={e => setCtrlForm({ ...ctrlForm, ip_address: e.target.value })} style={S.input} placeholder="192.168.1.50" /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Порт</label>
                <input type="number" value={ctrlForm.port} onChange={e => setCtrlForm({ ...ctrlForm, port: Number(e.target.value) })} style={S.input} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Нэвтрэх нэр</label>
                <input value={ctrlForm.username} onChange={e => setCtrlForm({ ...ctrlForm, username: e.target.value })} style={S.input} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Нууц үг</label>
                <input type="password" value={ctrlForm.password} onChange={e => setCtrlForm({ ...ctrlForm, password: e.target.value })} style={S.input} placeholder={editCtrl ? '(хоосон = хэвээр)' : ''} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Хаалганы тоо</label>
                <input type="number" value={ctrlForm.door_count} onChange={e => setCtrlForm({ ...ctrlForm, door_count: Number(e.target.value) })} style={S.input} /></div>
              {ctrlForm.controller_type === 'elevator' && (
                <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Зөвшөөрөх давхар</label>
                  <input value={ctrlForm.allowed_floors} onChange={e => setCtrlForm({ ...ctrlForm, allowed_floors: e.target.value })} style={S.input} placeholder="1,2,3,5,10" /></div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCtrlForm(false)} style={{ padding: '10px 20px', border: '1px solid #d5d0c4', borderRadius: 10, background: '#fff', cursor: 'pointer' }}>Болих</button>
              <button onClick={saveCtrl} disabled={!ctrlForm.name || !ctrlForm.ip_address} style={{ ...S.btn, opacity: (!ctrlForm.name || !ctrlForm.ip_address) ? 0.5 : 1 }}>Хадгалах</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CARD MODAL ═══ */}
      {showCardForm && (
        <div style={S.overlay} onClick={() => setShowCardForm(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1a3a2a', marginTop: 0 }}>
              {editCard ? 'Карт засах' : 'Карт бүртгэх'}
            </h2>
            <div style={{ display: 'grid', gap: 14 }}>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Картын дугаар *</label>
                <input value={cardForm.card_number} onChange={e => setCardForm({ ...cardForm, card_number: e.target.value })} style={S.input} placeholder="A1B2C3D4" disabled={!!editCard} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Нэр / тэмдэглэл</label>
                <input value={cardForm.card_label} onChange={e => setCardForm({ ...cardForm, card_label: e.target.value })} style={S.input} placeholder="Бат-101 гэр бүлийн" /></div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Тоот</label>
                <SearchableSelect
                  options={units.map((u: any) => ({ value: u.id, label: `${u.building}-${u.unit_number}` }))}
                  value={cardForm.unit_id}
                  onChange={v => setCardForm({ ...cardForm, unit_id: v })}
                  placeholder="Хайх: блок эсвэл тоот (жиш: 12-101)"
                />
              </div>
              <div><label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Тэмдэглэл</label>
                <textarea value={cardForm.notes} onChange={e => setCardForm({ ...cardForm, notes: e.target.value })} style={{ ...S.input, minHeight: 60 }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCardForm(false)} style={{ padding: '10px 20px', border: '1px solid #d5d0c4', borderRadius: 10, background: '#fff', cursor: 'pointer' }}>Болих</button>
              <button onClick={saveCard} disabled={!cardForm.card_number} style={{ ...S.btn, opacity: !cardForm.card_number ? 0.5 : 1 }}>Хадгалах</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
